import { getClient } from '@api/client';
import { API_URL } from '@api/apiDictionary';
import {
  confirmScreenshotMeasurementMock,
  createMeasurementMock,
  deleteMeasurementMock,
  lockMeasurementMock,
  unlockMeasurementMock,
  updateMeasurementMock,
} from '@api/mocks/readerMeasurements';
import {
  Measurement,
  MeasurementReqParams,
} from '@dynamika-shared/types/measurements';
import { replacePathParams } from 'src/utils/replacePathWithParams';
import { SegmentationMeasurementReqParams } from '@restore/segmentation/segmentation.type';

export const createMeasurement = async (
  clinicalTrialId: string,
  taskId: string,
  data: MeasurementReqParams | SegmentationMeasurementReqParams
) => {
  await createMeasurementMock(taskId);
  return getClient(API_URL.readerTaskMeasurements).post<
    Measurement,
    MeasurementReqParams
  >(replacePathParams(API_URL.readerTaskMeasurements, { taskId }), data, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
  });
};

export const updateMeasurement = async (
  measurementId: string,
  clinicalTrialId: string,
  taskId: string,
  data: MeasurementReqParams
) => {
  await updateMeasurementMock(taskId, measurementId, data);
  return getClient(API_URL.readerTaskMeasurement).put<
    Measurement,
    MeasurementReqParams
  >(
    replacePathParams(API_URL.readerTaskMeasurement, { taskId, measurementId }),
    data,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};

export const deleteMeasurement = async (
  measurementId: string,
  clinicalTrialId: string,
  taskId: string
) => {
  await deleteMeasurementMock(taskId, measurementId);
  return getClient(API_URL.readerTaskMeasurement).delete<
    Measurement,
    MeasurementReqParams
  >(
    replacePathParams(API_URL.readerTaskMeasurement, { taskId, measurementId }),
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};

export const lockMeasurement = async (
  measurementId: string,
  clinicalTrialId: string,
  taskId: string
) => {
  await lockMeasurementMock(taskId, measurementId);
  return getClient(API_URL.readerTaskMeasurementLock).post<
    Measurement,
    MeasurementReqParams
  >(
    replacePathParams(API_URL.readerTaskMeasurementLock, {
      taskId,
      measurementId,
    }),
    undefined,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};

export const unlockMeasurement = async (
  measurementId: string,
  clinicalTrialId: string,
  taskId: string
) => {
  await unlockMeasurementMock(taskId, measurementId);
  return getClient(API_URL.readerTaskMeasurementUnlock).post<
    Measurement,
    MeasurementReqParams
  >(
    replacePathParams(API_URL.readerTaskMeasurementUnlock, {
      taskId,
      measurementId,
    }),
    undefined,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};
export const confirmScreenshotMeasurement = async (
  measurementId: string,
  clinicalTrialId: string,
  taskId: string,
  // When the lock status produces no screenshot (RANO TL Absent/Merged/NE,
  // NL Absent/NE), the host confirms the screenshot is deliberately absent —
  // sent as the `?notAvailable=true` query param. Omitted for the normal
  // post-upload confirm (screenshot exists).
  notAvailable = false
) => {
  await confirmScreenshotMeasurementMock(taskId, measurementId);
  return getClient(API_URL.readerTaskMeasurementScreenshot).post<
    Measurement,
    MeasurementReqParams
  >(
    replacePathParams(API_URL.readerTaskMeasurementScreenshot, {
      taskId,
      measurementId,
    }),
    undefined,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
      ...(notAvailable ? { params: { notAvailable: true } } : {}),
    }
  );
};
